import React, { useState, useCallback, useRef, useEffect, useMemo, useContext } from 'react';
import { View, Platform, useWindowDimensions, FlatList, RefreshControl, Pressable, ActivityIndicator } from 'react-native';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useProjectsQuery, createPaginationState, updatePaginationState, resetPaginationPage } from '@utils/projectQueries';
import { Pagination } from '@components/Global/Pagination/Pagination';
import { Entypo, FontAwesome, SimpleLineIcons, MaterialIcons } from '@expo/vector-icons';
import AnimatedLottieView from '@utils/lottieAnimation';
import * as Animatable from 'react-native-animatable';
import DefaultScreen from '@components/Global/DefaultScreenView';
import AddProjectModal from './Components/AddProject';
import AIProjectCreationModal from '@navigation/NavigationStacks/Components/AIProjectCreationModal';
import UserInfoModal from './Components/OwnerInfoModal';
import ShareUserModal from '@components/modals/ShareUserModal/ShareUserModal';
import DeleteConfirmModal from '@components/modals/DeleteConfirmModal/DeleteConfirmModal';
import parseByDelimiter from '@utils/parseByDelimiter';
import RenderProject from './Components/ProjectItem';
import GlobalTutorial from '@components/TutorialComponent/GlobalTutorial';
import { carouselData } from './Tutorial/CarouselData';
import styles from '@stylesheet';
import filter from 'lodash.filter';
import apis from '@api/api';
import { DefaultRootState, store } from '@reduxLocal/persistState';
import { ToolbarContext } from '@hooks/toolbarContext';
import SwipeableRobot, { RobotHandle } from '@components/RobotHelper/SwipeableRobot';
import { debounce } from 'lodash';
import LinkGenModal from '../DataScreen/CaptureScreen/Components/LinkGenModal';
import { useDeviceType } from '@hooks/useDeviceType';
import { HOST } from '@api/constant';
import LottieView from 'lottie-react-native';
import Button from '@components/UI/Button/Button';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { XStack, Text, YStack, H2 } from 'tamagui';
import { useTranslation } from 'react-i18next';

const PROJECT_WIDTH = 400;

const ProjectManagementScreen = ({ navigation }) => {
  const { width } = useWindowDimensions();
  const [refreshing, setRefreshing] = useState(false);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [columns, setColumns] = useState(Math.max(1, Math.floor(width / (PROJECT_WIDTH + 20))));
  const [listHorizontal, setListHorizontal] = useState(Platform.OS === 'web' && width > 800);
  const [query, setQuery] = useState('');

  // Pagination state
  const [paginationState, setPaginationState] = useState(() =>
    createPaginationState({ currentPage: 1, pageSize: 12 })
  );
  const bottomDrawerRef = useRef(null);
  const aiProjectModalRef = useRef(null);
  const infoModalRef = useRef(null);
  const shareUserRef = useRef(null);
  const [activeProject, setActiveProject] = useState(null);
  const { setProjectContext } = useContext(ToolbarContext);
  const [help, setHelp] = useState(true);
  const robotRef = useRef<RobotHandle>(null);
  const [carouselProjectData, setCarouselProjectData] = useState(carouselData); 
  const linkGenRef = useRef(null);
  const { isTablet } = useDeviceType();
  const theme = themeSelector();
  const { t } = useTranslation();
  const containerRef = useRef<View>(null);
  const [containerWidth, setContainerWidth] = useState(width);

  // Header collapsible state (internal to this component)
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);


  const textHelp = [
    'Manage your projects efficiently and effectively using AI assistance',
    'Co-ordinate and collaborate with your team members on projects',
    'Create templates for your projects to save time and effort',
    'Stay on top of your project deadlines and milestones with our AI assistant and real-time notifications',
  ]

  // Paginated projects query
  const { data: paginatedData, isLoading, refetch } = useProjectsQuery({
    pagination: paginationState,
    enabled: true
  });

  // Fallback query for backward compatibility
  const { data: fallbackData } = useQuery({
    queryKey: ['projectData-fallback'],
    queryFn: async () => {
      setCarouselProjectData(carouselData => [{ title: 'Loading Projects', caption: 'Loading your projects', image: Platform.OS === 'web' ? <View style={{ minHeight: 150, maxHeight: 150, top: -30, minWidth: 150, backgroundColor: 'transparent' }} /> : <LottieView source={require("@assets/animations/spin-leaf-loader.json")} autoPlay style={{ minHeight: 150, maxHeight: 150, top: -30, minWidth: 150 }} loop /> }, ...carouselData]);
      const data = await apis.getProjects()
        .then(res => res.data.data || res.data || []);
      setCarouselProjectData(carouselData);
      return data;
    },
    enabled: !paginatedData,
    staleTime: 1000 * 60 * 15, // 15 minutes
    retry: 2,
  });

  // Use paginated data or fallback
  const projectData = paginatedData?.data?.records || fallbackData || [];

  // Update filteredProjects when projectData changes
  useEffect(() => {
    if (projectData && projectData.length > 0) {
      setFilteredProjects(projectData);
    }
  }, [projectData]);

  const dataFilter = 'all';

  // Function to calculate columns based on container width
  const calculateColumns = useCallback((parentWidth) => {
    // Calculate number of columns based on available width and item width (400px)
    // Add some padding/margin considerations (e.g., 40px container padding + 20px gap between items)
    const containerPadding = 40; // 20px on each side
    const itemGap = 20; // Gap between items
    const availableWidth = parentWidth - containerPadding;
    
    // Calculate how many items can fit with gaps
    // Formula: availableWidth >= (columns * PROJECT_WIDTH) + ((columns - 1) * gap)
    // Rearranged: columns = (availableWidth + gap) / (PROJECT_WIDTH + gap)
    const calculatedColumns = Math.floor((availableWidth + itemGap) / (PROJECT_WIDTH + itemGap));

    // Always ensure at least 1 column, maximum reasonable columns for screen size
    const finalColumns = Math.max(1, Math.min(calculatedColumns, 6));

    return finalColumns;
  }, []);

  // Handle container layout changes
  const handleContainerLayout = useCallback((event) => {
    const { width: measuredWidth } = event.nativeEvent.layout;
    if (measuredWidth !== containerWidth) {
      setContainerWidth(measuredWidth);
    }
  }, [containerWidth]);

  useEffect(() => {
    if (Platform.OS === 'web') {
    setProjectContext({ refreshFn: refetch })
    }
  }, [])

  useEffect(() => {
    const debounceResize = debounce(() => {
      const parentWidth = containerWidth || width;
      const calculatedColumns = calculateColumns(parentWidth);


      if (parentWidth > 1600 || isTablet) {
        setColumns(calculatedColumns);
        setListHorizontal(false);
      } else {
        // For smaller screens, use single column
        setListHorizontal(false);
        setColumns(Math.max(1, calculatedColumns));
      }
    }, 100); // Reduced debounce time for faster response
    
    debounceResize();
    
    return () => {
      debounceResize.cancel();
    };
  }, [containerWidth, width, isTablet, calculateColumns]);

  const handleSearch = useCallback((text) => {
    const formattedQuery = text.toLowerCase();
    setQuery(text);
    const filteredData = filter(projectData, project => contains(project, formattedQuery));
    setFilteredProjects(filteredData);
  }, [projectData]);

  // Pagination handlers
  const handlePageChange = useCallback((page: number) => {
    setPaginationState(prev => updatePaginationState(prev, { currentPage: page }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPaginationState(prev => resetPaginationPage(updatePaginationState(prev, { pageSize })));
  }, []);

  const handleSortByChange = useCallback((sortBy: string) => {
    setPaginationState(prev => resetPaginationPage(updatePaginationState(prev, { sortBy })));
  }, []);

  const handleSortOrderChange = useCallback((sortOrder: 'asc' | 'desc') => {
    setPaginationState(prev => resetPaginationPage(updatePaginationState(prev, { sortOrder })));
  }, []);

  const contains = ({ title, description }, query) => {
    return title.toLowerCase().includes(query) || description.toLowerCase().includes(query);
  };

  const changeListView = useCallback(() => {
    if (listHorizontal) {
      const parentWidth = containerWidth || width;
      const calculatedColumns = calculateColumns(parentWidth);
      setColumns(calculatedColumns);
    }
    setListHorizontal(!listHorizontal);
  }, [listHorizontal, containerWidth, width, calculateColumns]);

  const handleModalState = useCallback((item) => {
    setActiveProject(item);
    setDeleteModalVisible(true);
  }, []);

  const _handleShareUser = useCallback((item) => {
    shareUserRef.current?.openModal(item);
  }, [shareUserRef]);

  const _handleSelectUser = useCallback((item) => {
    infoModalRef.current?.toggleVisibility(item);
  }, []);

  const handleShowHelp = () => {
    // setHelp(true);
    robotRef.current!.showHelp();
  }

  const HeaderComponent = useCallback(() => {
    return (
      <Animatable.View
        animation="fadeInDown"
        duration={800}
        style={{
          backgroundColor: theme ? "#ffffff" : "#1e293b",
          borderRadius: 20,
          padding: 24,
          marginVertical: 16,
          marginHorizontal: 20,
          borderWidth: 1,
          borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: isHeaderCollapsed ? 0 : 20 }}>
          <View style={{
            backgroundColor: theme ? "#dbeafe" : "#0c4a6e",
            borderRadius: 12,
            padding: 12,
            marginRight: 16
          }}>
            <MaterialIcons
              name="folder-open"
              size={28}
              color={theme ? "#0ea5e9" : "#38bdf8"}
            />
          </View>
          <View style={{ flex: 1 }}>
            <H2 style={{
              color: theme ? "#1e293b" : "#f1f5f9",
              fontWeight: '700',
              marginBottom: 4,
              fontSize: 24
            }}>
              Project Management
            </H2>
            {!isHeaderCollapsed && (
              <Text style={{
                color: theme ? "#64748b" : "#94a3b8",
                fontSize: 14
              }}>
                Manage and organize your projects efficiently
              </Text>
            )}
          </View>
          <Pressable
            onPress={() => setIsHeaderCollapsed(!isHeaderCollapsed)}
            style={{
              padding: 8,
              borderRadius: 8,
              backgroundColor: theme ? "#f1f5f9" : "#1e293b"
            }}
          >
            <MaterialIcons
              name={isHeaderCollapsed ? "expand-more" : "expand-less"}
              size={24}
              color={theme ? "#64748b" : "#94a3b8"}
            />
          </Pressable>
        </View>

        {/* Collapsible Content */}
        {!isHeaderCollapsed && (
          <>
            {/* Stats Grid */}
            <XStack gap={12} flexWrap="wrap" justifyContent="center" alignItems="center">
          {[
            {
              title: 'Total Projects',
              value: filteredProjects.length.toString(),
              icon: 'folder',
              color: theme ? "#0ea5e9" : "#38bdf8",
              bgColor: theme ? "#dbeafe" : "#0c4a6e"
            },
            {
              title: 'Active Projects',
              value: filteredProjects.filter(p => p.status === 'active').length.toString(),
              icon: 'play-arrow',
              color: theme ? "#059669" : "#10b981",
              bgColor: theme ? "#d1fae5" : "#064e3b"
            },
            {
              title: 'Completed',
              value: filteredProjects.filter(p => p.status === 'completed').length.toString(),
              icon: 'check-circle',
              color: theme ? "#7c3aed" : "#a78bfa",
              bgColor: theme ? "#ede9fe" : "#312e81"
            },
            {
              title: 'In Progress',
              value: filteredProjects.filter(p => p.status === 'in-progress' || !p.status).length.toString(),
              icon: 'schedule',
              color: theme ? "#f59e0b" : "#fbbf24",
              bgColor: theme ? "#fef3c7" : "#451a03"
            }
          ].map((stat, index) => (
            <Animatable.View
              key={index}
              animation="bounceIn"
              delay={index * 200}
              style={{
                flexBasis: Platform.OS === 'web' ? '22%' : '45%',
                flexGrow: 1,
                minWidth: 140,
                maxWidth: Platform.OS === 'web' ? 200 : undefined,
                backgroundColor: stat.bgColor,
                borderRadius: 16,
                padding: 16,
                alignItems: 'center',
                marginBottom: 8,
                borderWidth: 1,
                borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
              }}
            >
              <MaterialIcons
                name={stat.icon as any}
                size={32}
                color={stat.color}
                style={{ marginBottom: 8 }}
              />
              <Text style={{
                fontSize: 24,
                fontWeight: '800',
                color: stat.color,
                marginBottom: 4
              }}>
                {stat.value}
              </Text>
              <Text style={{
                fontSize: 12,
                fontWeight: '600',
                color: theme ? "#64748b" : "#94a3b8",
                textAlign: 'center'
              }}>
                {stat.title}
              </Text>
            </Animatable.View>
          ))}
        </XStack>
          </>
        )}
      </Animatable.View>
    )
  }, [filteredProjects, theme, isHeaderCollapsed, aiProjectModalRef, navigation, dataFilter, t]);

  const MemoizedProjectData = React.memo(RenderProject);

  const qrCodeCallback = (item) => {
    linkGenRef.current?.openModal(item);
  };

  const _renderItem = useCallback(({ item, index }) => {
    return (
      <MemoizedProjectData
        navigation={navigation}
        qrCodeCallback={qrCodeCallback}
        item={item}
        maxWidth={PROJECT_WIDTH}
        index={index}
        horizontal={listHorizontal}
        _handleModalState={handleModalState}
        _handleSelectUser={_handleSelectUser}
        _handleShareUser={_handleShareUser}
      />
    )
  },
    [filteredProjects]);

  console.log('columns', columns);

  return (
      <DefaultScreen
        backButton
      aiSearch
      aiCallback={async (text) => {
        try {
          // Call AI to generate project from description
          const response = await apis.generateAIProjectFromDescription(text, {
            industry: /* get from context if available */ 'general',
            projectType: 'standard',
            timeline: 'medium',
            teamSize: 'small'
          });

          if (response.data.success) {
            // Refresh projects list
            await refetch();
            // Show success message
            store.dispatch({
              type: 'SHOW_AUTH_MODAL',
              data: {
                content: 'AI project generated successfully! Check your projects list.',
                type: 'success'
              }
            });
          }
        } catch (err) {
          console.error('AI project generation failed:', err);
          store.dispatch({
            type: 'SHOW_AUTH_MODAL',
            data: {
              content: 'AI project generation failed. Please try creating manually.',
              type: 'error'
            }
          });
        }
      }}
        screenTitle={'Project Management'}
      containerStyle={{ backgroundColor: theme ? "white" : "black", flex: 1, paddingTop: 10, paddingBottom: Platform.select({ web: 70 }) }}
        scrollDisabled
        searchBar={{
          callback: handleSearch,
          key: 'projectSearch',
          placeHolder: 'Search Projects',
          customIcons: (Platform.OS === 'web' || isTablet) ? [{
              icon: <Entypo name="help" size={18} color={"white"} />,
              function: handleShowHelp,
              functionName: 'Show Help',
            },
            {
              icon: <MaterialIcons name="auto-awesome" size={18} color="white" />,
              function: () => aiProjectModalRef.current?.openModal(),
              functionName: 'Create AI Project',
            },
            {
              icon: <FontAwesome name="plus-circle" size={18} color="white" />,
              function: () => { Platform.OS === 'web' ? bottomDrawerRef.current?.openModal() : bottomDrawerRef.current?.present() },
              functionName: 'Add Project',
            }
          ] : [
            {
              icon: <FontAwesome name="plus-circle" size={14} color="white" />,
              function: () => { bottomDrawerRef.current?.present() },
              functionName: 'Add Project',
            },
              {
                icon: <MaterialIcons name="auto-awesome" size={14} color="white" />,
                function: () => aiProjectModalRef.current?.openModal(),
                functionName: 'Create AI Project',
              },
              {
                icon: <Entypo name="help" size={14} color={"white"} />,
                function: handleShowHelp,
                functionName: 'Show Help',
              }
          ]
        }}
      >
      <LinkGenModal caption='You can easily share your projects using the QR code' heading='Share your project' url={__DEV__ ? `${HOST}/projects/` : "https://api.smartsaas.pro/projects/"} ref={linkGenRef} />
        <UserInfoModal ref={infoModalRef} />
        <ShareUserModal ref={shareUserRef} title={activeProject?.title} shareFunction={apis.shareProject} />
        <DeleteConfirmModal deleteFunction={apis.deleteProject} itemS={activeProject} title={parseByDelimiter(activeProject?.name || "")} refreshFunction={refetch} setState={setDeleteModalVisible} visibleState={deleteModalVisible} />
      {/* @ts-ignore */}
      <AIProjectCreationModal ref={aiProjectModalRef} callBack={refetch} />
      <View
        ref={containerRef}
        onLayout={handleContainerLayout}
        style={{ flexDirection: 'column', flex: 1, alignSelf: "stretch" }}
      >
        {/* FlatList for project rendering */}
        <FlatList
          key={`projects-${columns}-${listHorizontal ? 'horizontal' : 'vertical'}`}
          style={{ flex: 1, alignSelf: 'stretch' }}
          data={filteredProjects?.reverse() || []}
          horizontal={listHorizontal}
          nestedScrollEnabled
          extraData={projectData}
          maxToRenderPerBatch={4}
          windowSize={6}
          onRefresh={refetch}
          refreshing={isLoading}
          directionalLockEnabled
          contentContainerStyle={{ paddingVertical: 20, paddingBottom: listHorizontal ? 0 : 150, maxHeight: listHorizontal ? "100%" : undefined }}
          numColumns={listHorizontal ? undefined : columns}
          showsVerticalScrollIndicator={false}
          indicatorStyle='black'
          renderItem={_renderItem}
          ListHeaderComponent={<HeaderComponent />}
          ListEmptyComponent={(
            <View style={{ flex: 1, alignSelf: 'center', alignContent: 'center', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              {query != '' ?
                <View style={[styles.noteContainer, { flex: 1, margin: 50, padding: 5, paddingBottom: 20 }]}>
                  {Platform.OS === 'web' ? <View style={{ height: 100, width: 100, flex: 1, backgroundColor: 'transparent' }} /> : <AnimatedLottieView source={require('@assets/animations/88006-warning-yellow.json')} autoPlay loop={false} style={{ height: 100, width: 100, flex: 1 }} />}
                  <Text style={{ flex: 1 }}>No items found matching query</Text>
                </View>
                :
                <View style={{ gap: 30, margin: 30 }}>
                  <View style={[styles.centerEverything, { gap: 30, flex: 1, maxWidth: "100%", alignSelf: "stretch" }]}>
                    {
                      isLoading &&
                      <ActivityIndicator size="large" color="white" />
                    }
                    <GlobalTutorial customHeight={350} customWidth={300} carouselData={carouselProjectData} />
                    <Button
                      buttonText='Add Project'
                      onPress={() => { Platform.OS === 'web' ? bottomDrawerRef.current?.openModal() : bottomDrawerRef.current?.present() }}
                      customStyle={{ minWidth: 150, maxHeight: 60, borderColor: 'rgb(90,192,234)', marginVertical: 0 }}
                    />
                  </View>
                </View>
              }
            </View>
          )}
          ListFooterComponentStyle={{ alignSelf: 'center', maxWidth: '100%', justifyContent: 'center', paddingBottom: 50 }}
          refreshControl={listHorizontal ? null : <RefreshControl refreshing={refreshing} onRefresh={refetch} />}
          scrollEnabled={true}
          keyExtractor={item => item._id}
        />

        {/* Integrated Pagination and Sorting Controls */}
        {paginationState && (
          <Pagination
            pagination={paginatedData?.data?.pagination ? {
              currentPage: paginatedData.data.pagination.currentPage || 1,
              totalPages: paginatedData.data.pagination.totalPages || 1,
              totalRecords: paginatedData.data.pagination.totalRecords || 0,
              hasNextPage: paginatedData.data.pagination.hasNextPage || false,
              hasPrevPage: paginatedData.data.pagination.hasPrevPage || false,
              nextPage: paginatedData.data.pagination.nextPage,
              prevPage: paginatedData.data.pagination.prevPage
            } : {
              currentPage: 1,
              totalPages: 1,
              totalRecords: filteredProjects?.length || 0,
              hasNextPage: false,
              hasPrevPage: false,
              nextPage: undefined,
              prevPage: undefined
            }}
            pageSize={paginationState.pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={[6, 12, 24, 48]}
            showPageSizeSelector={true}
            showTotalRecords={true}
            compact={true}
            showSorting={true}
            sortBy={paginationState.sortBy}
            sortOrder={paginationState.sortOrder}
            onSortByChange={handleSortByChange}
            onSortOrderChange={handleSortOrderChange}
            sortByOptions={[
              { value: 'createdAt', label: 'Created Date' },
              { value: 'updatedAt', label: 'Updated Date' },
              { value: 'title', label: 'Title' },
              { value: 'name', label: 'Name' }
            ]}
            style={{ marginTop: 20 }}
            />
          )}
      </View>
      <AddProjectModal ref={bottomDrawerRef} notContext callBack={refetch} />
      <View style={{ position: "absolute", bottom: isTablet ? 0 :100, left: isTablet && 150 }}>
        {/* <SwipeableRobot ref={robotRef} textHelp={textHelp} /> */}
      </View>
    </DefaultScreen>
  )
};

export default ProjectManagementScreen;
